import { logError } from 'remoteLogging';
import { getShieldInformation } from 'Environment/ChromeApp/ChromeAppBridge';
import {
	ShieldInformationResult,
	ShieldStateInfoPayload,
} from 'Environment/ChromeApp/types';

/**
 * Module-level cache of the latest shield-state information supplied by the
 * Chrome App (CWA). These values are attached to every CWA POI telemetry event.
 *
 * A singleton (rather than React context) is used because the POI events are
 * emitted from non-React launch code (`ChromeAppLaunch.ts`). The cache is seeded
 * after login via `refreshChromeAppShieldInformation` and kept current by the
 * `updateShieldInformation` push message handled in `chromeApp.ts`.
 */
let cachedShieldInformation: ShieldInformationResult = {};

export function getChromeAppShieldInformation(): ShieldInformationResult {
	return cachedShieldInformation;
}

export function setChromeAppShieldInformation(info?: ShieldInformationResult): void {
	if (info) {
		cachedShieldInformation = { ...cachedShieldInformation, ...info };
	}
}

/**
 * Requests the current shield information from CWA (`getShieldInformationReq`)
 * and caches the result. Errors are swallowed (logged) so that a failure to
 * fetch shield info never blocks login or launch.
 */
export async function refreshChromeAppShieldInformation(
	workload: ShieldStateInfoPayload
): Promise<void> {
	try {
		const response = await getShieldInformation(workload);
		setChromeAppShieldInformation(response?.result);
	} catch (error) {
		logError(error, {
			customMessage: 'Failed to fetch shield information from Chrome App',
		});
	}
}
